import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  Button,
  DialogModal,
  Drawer,
  PanelHeader,
  TextController,
  TextAreaController,
  ColorController,
  SwitchController,
  Text,
} from "@pushpress/shared-components";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { removeTypeName } from "../../../utils/graphqlUtils";

import {
  useCreateCalendarClassTypeMutation,
  useGetCalendarClassTypeQuery,
  useUpdateCalendarClassTypeMutation,
} from "./ClassTypes-generated.hooks";

export interface Form {
  uuid: string;
  name: string;
  color: string;
  isFreeSession: boolean;
  isActive: boolean;
  description: string;
}

interface ClassTypeDrawerProps {
  uuid?: string;
  visible: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

function ClassTypeDrawer(props: ClassTypeDrawerProps) {
  const { visible } = props;

  return (
    <Drawer
      visible={visible}
      anchor="right"
      width="400px"
      disableClickAway
      disableEscapeKeyDown
    >
      <ClassTypeDrawerContent {...props} />
    </Drawer>
  );
}

function ClassTypeDrawerContent(props: ClassTypeDrawerProps) {
  const { uuid, onClose, onSuccess } = props;

  const [saving, setSaving] = useState(false);

  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);

  const classTypeQuery = useGetCalendarClassTypeQuery({
    variables: { uuid: uuid! },
    skip: !uuid,
    fetchPolicy: "no-cache",
  });

  const [updateMutation] = useUpdateCalendarClassTypeMutation();

  const [createMutation] = useCreateCalendarClassTypeMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<Form>({
    mode: "onTouched",
  });

  const handleClose = () => {
    if (isDirty) {
      setConfirmCloseVisible(true);
    } else {
      onClose();
    }
  };

  const save = async (values: Form) => {
    try {
      setSaving(true);
      if (uuid) {
        await updateMutation({ variables: { input: values } });
      } else {
        await createMutation({ variables: { input: values } });
      }
      toast.success("Class type saved", defaultToastOptions);
      onSuccess();
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    handleSubmit(save)();
  };

  useEffect(() => {
    if (classTypeQuery?.data) {
      const values = classTypeQuery?.data?.getCalendarClassType;
      reset(removeTypeName(values));
    }
  }, [classTypeQuery?.data]);

  return (
    <>
      <PanelHeader title="Class type">
        <PanelHeader.ActionMenu>
          <Button
            text="Close"
            buttonType="secondary"
            size="medium"
            onClick={handleClose}
          />
          <Button
            text="Save"
            buttonType="primary"
            size="medium"
            type="button"
            loading={saving}
            onClick={handleSave}
          />
        </PanelHeader.ActionMenu>
      </PanelHeader>
      <div style={{ padding: 24 }}>
        <form onSubmit={handleSubmit(save)}>
          <TextController
            label="Name"
            control={control}
            name="name"
            required
            autoComplete="off"
            maxLength={64}
          />
          <ColorController
            label="Color"
            control={control}
            name="color"
            required
            autoComplete="off"
          />
          <SwitchController
            label="Free session"
            control={control}
            name="isFreeSession"
          />
          <Text variant="body-md" color="wolf-600" className="mb-3">
            If enabled, classes of this type will NOT count against limited
            plans or punchcards
          </Text>
          <TextAreaController
            control={control}
            name="description"
            label="Description"
          />
        </form>
      </div>
      <DialogModal
        title="Discard unsaved changes?"
        visible={confirmCloseVisible}
        primaryBtnTitle="Discard"
        secondaryBtnTitle="Go back"
        primaryHandler={onClose}
        secondaryHandler={() => setConfirmCloseVisible(false)}
        closeModal={() => setConfirmCloseVisible(false)}
      />
    </>
  );
}

export default ClassTypeDrawer;
